import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';
import { updateCompanySubscription } from '../../../services/companyService';

const SubscriptionModal = ({ isOpen, onClose, onSubmit, company }) => {
  const [formData, setFormData] = useState({
    subscriptionTier: 'Starter',
    billingCycle: 'monthly',
    autoRenew: true
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (company) {
      setFormData({
        subscriptionTier: company?.subscriptionTier || 'Starter',
        billingCycle: company?.billingCycle || 'monthly',
        autoRenew: company?.autoRenew ?? true
      });
    }
  }, [company, isOpen]);

  const subscriptionOptions = [
    { value: 'Starter', label: 'Starter' },
    { value: 'Professional', label: 'Professional' },
    { value: 'Enterprise', label: 'Enterprise' }
  ];

  const billingCycleOptions = [
    { value: 'monthly', label: 'Monthly' },
    { value: 'annual', label: 'Annual (Save 20%)' }
  ];

  const getPricing = () => {
    const prices = {
      'Starter': { monthly: 29, annual: 278 },
      'Professional': { monthly: 99, annual: 950 },
      'Enterprise': { monthly: 'Custom', annual: 'Custom' }
    };
    return prices?.[formData?.subscriptionTier]?.[formData?.billingCycle] || 0;
  };

  const getFeatures = () => {
    const features = {
      'Starter': [
        'Up to 5 users',
        'Basic accounting features',
        'Email support',
        '1 company account',
        'Standard reports'
      ],
      'Professional': [
        'Up to 25 users',
        'Advanced accounting features',
        'Priority support',
        'Up to 5 company accounts',
        'Custom reports',
        'API access'
      ],
      'Enterprise': [
        'Unlimited users',
        'All features included',
        'Dedicated support',
        'Unlimited company accounts',
        'Advanced analytics',
        'Custom integrations',
        'SLA guarantee'
      ]
    };
    return features?.[formData?.subscriptionTier] || [];
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      await updateCompanySubscription(company?.id, formData);
      onSubmit();
      onClose();
    } catch (error) {
      console.error('Error updating subscription:', error);
      alert('Failed to update subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen || !company) return null;

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-1030 flex items-center justify-center p-4">
      <div className="bg-card rounded-lg shadow-2xl border border-border w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 md:p-6 border-b border-border">
          <div>
            <h2 className="font-heading font-semibold text-xl md:text-2xl text-foreground">Manage Subscription</h2>
            <p className="text-sm text-muted-foreground mt-1">{company?.businessName}</p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={onClose}
            iconName="X"
            aria-label="Close modal"
          />
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-4 md:p-6">
          <div className="space-y-6">
            <div>
              <h3 className="font-heading font-semibold text-base text-foreground mb-4">Subscription Plan</h3>
              <Select
                label="Plan Tier"
                value={formData?.subscriptionTier}
                onChange={(value) => setFormData({ ...formData, subscriptionTier: value })}
                options={subscriptionOptions}
              />
            </div>

            <div>
              <h3 className="font-heading font-semibold text-base text-foreground mb-4">Billing Cycle</h3>
              <Select
                label="Billing Frequency"
                value={formData?.billingCycle}
                onChange={(value) => setFormData({ ...formData, billingCycle: value })}
                options={billingCycleOptions}
              />
            </div>

            <div className="bg-primary/5 rounded-lg p-4 border border-primary/20">
              <div className="flex items-center justify-between mb-4">
                <h4 className="font-heading font-semibold text-lg text-foreground">Plan Features</h4>
                <div className="text-right">
                  <p className="text-2xl font-heading font-bold text-primary">
                    {typeof getPricing() === 'number' ? `$${getPricing()}` : getPricing()}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    per {formData?.billingCycle === 'monthly' ? 'month' : 'year'}
                  </p>
                </div>
              </div>
              <ul className="space-y-2">
                {getFeatures()?.map((feature, index) => (
                  <li key={index} className="flex items-center gap-2">
                    <Icon name="Check" size={16} className="text-success flex-shrink-0" />
                    <span className="text-sm text-foreground">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="autoRenew"
                checked={formData?.autoRenew}
                onChange={(e) => setFormData({ ...formData, autoRenew: e?.target?.checked })}
                className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
              />
              <label htmlFor="autoRenew" className="text-sm text-foreground">
                Enable auto-renewal
              </label>
            </div>

            <div className="bg-muted/30 rounded-lg p-4">
              <h4 className="font-heading font-semibold text-sm text-foreground mb-2">Current Subscription</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">Current Plan</p>
                  <p className="text-sm text-foreground font-medium">{company?.subscriptionTier}</p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Status</p>
                  <p className="text-sm text-success font-medium">Active</p>
                </div>
              </div>
            </div>
          </div>
        </form>

        <div className="flex items-center justify-end gap-3 p-4 md:p-6 border-t border-border">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            variant="default"
            onClick={handleSubmit}
            disabled={loading}
            iconName={loading ? 'Loader2' : 'CreditCard'}
          >
            {loading ? 'Updating...' : 'Update Subscription'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionModal;
